import {
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
  subQuarters,
  subYears,
} from "date-fns";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";

export const getSafeFormattedDate = (
  dateStr?: Date | string,
  formatStr = "dd MMM, yyyy",
): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? "" : format(date, formatStr);
};

export const useDueDateFilter = (
  selectedIds: string[],
  onUpdate: (values: string[]) => void,
) => {
  const [open, setOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [activeKey, setActiveKey] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const start = selectedIds[0] ? new Date(selectedIds[0]) : undefined;
      const end = selectedIds[1] ? new Date(selectedIds[1]) : undefined;
      setDateRange({
        from: start && !isNaN(start.getTime()) ? start : undefined,
        to: end && !isNaN(end.getTime()) ? end : undefined,
      });
      setActiveKey(null);
    }
  }, [open, selectedIds]);

  const handleRangeSelect = (key: string) => {
    const today = new Date();
    const ranges: Record<string, [Date, Date]> = {
      thisMonth: [startOfMonth(today), endOfMonth(today)],
      lastMonth: [
        startOfMonth(subMonths(today, 1)),
        endOfMonth(subMonths(today, 1)),
      ],
      last6Months: [startOfMonth(subMonths(today, 6)), endOfMonth(today)],
      thisQuarter: [startOfQuarter(today), endOfQuarter(today)],
      lastQuarter: [
        startOfQuarter(subQuarters(today, 1)),
        endOfQuarter(subQuarters(today, 1)),
      ],
      thisYear: [startOfYear(today), endOfYear(today)],
      lastYear: [
        startOfYear(subYears(today, 1)),
        endOfYear(subYears(today, 1)),
      ],
    };

    const range = ranges[key];
    if (range) {
      setDateRange({ from: range[0], to: range[1] });
    }
    setActiveKey(key);
  };

  const clearStartDate = () => {
    setDateRange((prev) => ({ ...prev, from: undefined }));
    setActiveKey(null);
  };

  const clearEndDate = () => {
    setDateRange((prev) => ({ ...prev, to: undefined }));
    setActiveKey(null);
  };

  const onSave = () => {
    if (!dateRange.from && !dateRange.to) {
      onUpdate([]);
    } else {
      const start = dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : "";
      const end = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : "";
      onUpdate([start, end]);
    }
    setOpen(false);
  };

  const onCancel = () => {
    setDateRange({ from: undefined, to: undefined });
    setActiveKey(null);
    onUpdate([]);
    setOpen(false);
  };

  const startDate = selectedIds[0];
  const endDate = selectedIds[1];
  const isAllSelected = !!startDate && !!endDate;
  const color = isAllSelected ? "#5A8DF1" : "#79808A";

  const getButtonLabel = () => {
    const startStr = startDate ? getSafeFormattedDate(startDate, "dd MMM") : "";
    const endStr = endDate ? getSafeFormattedDate(endDate, "dd MMM") : "";

    if (startStr && endStr) return `Due date: ${startStr} - ${endStr}`;
    if (startStr) return `Due date: ${startStr}`;
    return "Due date";
  };

  return {
    open,
    setOpen,
    dateRange,
    setDateRange,
    activeKey,
    setActiveKey,
    handleRangeSelect,
    clearStartDate,
    clearEndDate,
    onSave,
    onCancel,
    getHeaderLabel: getSafeFormattedDate,
    getSafeFormattedDate,
    getButtonLabel,
    isAllSelected,
    color,
  };
};
